import { z } from 'zod';
import { NsfwLevel } from './enums.ts';

/**
 * The companion builder contracts — the typed shape of `girlfriends.persona` and
 * `girlfriends.appearance` (both `jsonb`). They flow api↔backend↔frontend: the
 * builder routes parse an incoming body with these, the create service compiles
 * them, and the builder UI renders forms off them (one source of truth, no
 * per-app copies). Structure lives here; the untrusted-content policy pass (minor
 * framing, jailbreak, impersonation) is `validate-persona` in `apps/backend`.
 *
 * See docs/idea/companions.md § "The builder" + § "Persona → system prompt".
 */

/**
 * `appearance.age_range` — **ADULT-ONLY, structurally**. No minor bracket exists,
 * so the builder simply cannot encode a minor (compliance red line 1); the
 * persona validator additionally scans free text for minor framing (defense in
 * depth). Add a bracket here, nowhere else.
 */
export const AgeRange = z.enum(['18-24', '25-34', '35-44', '45-54', '55-plus']);
export type AgeRange = z.infer<typeof AgeRange>;

/** `appearance.style` — the render style the reference image is generated in. */
export const AppearanceStyle = z.enum(['2d', '3d']);
export type AppearanceStyle = z.infer<typeof AppearanceStyle>;

/**
 * `girlfriends.appearance` — builder attributes that compile to the reference
 * image's generation prompt. `ageRange` is adult-only (above); free-text fields
 * are length-capped and moderated on write.
 */
export const Appearance = z.object({
  style: AppearanceStyle,
  body: z.string().min(1).max(40),
  hair: z.string().min(1).max(60),
  eyes: z.string().min(1).max(40),
  ethnicity: z.string().min(1).max(40),
  ageRange: AgeRange,
  outfit: z.string().max(80).optional(),
  tags: z.array(z.string().min(1).max(40)).max(20).default([]),
});
export type Appearance = z.infer<typeof Appearance>;

/**
 * `girlfriends.persona` — builder **inputs** (name, traits, backstory,
 * relationship, speaking style, intensity), NEVER raw model instructions. The
 * agent compiles these into delimited system-prompt slots at run time
 * (`compile-persona`); the text is user-authored and therefore untrusted.
 */
export const Persona = z.object({
  name: z.string().min(1).max(40),
  traits: z.array(z.string().min(1).max(60)).max(20).default([]),
  backstory: z.string().max(2000).default(''),
  relationship: z.string().max(200).default(''),
  speakingStyle: z.string().max(200).default(''),
  nsfwLevel: NsfwLevel,
});
export type Persona = z.infer<typeof Persona>;
